import { ChevronDownIcon, ChevronUpIcon } from "@radix-ui/react-icons";
import * as React from "react";
import {
  Button,
  Group,
  NumberField,
  NumberFieldProps,
  Input as ReactAriaInput,
} from "react-aria-components";

import {
  ColorContext,
  ColorVariant,
  theme,
} from "@/components/canvas-editor/components/Container";
import { cn } from "@/lib/utils";

export interface InputProps extends React.ComponentProps<"input"> {
  onValueChange?: (value: string) => void;
  ignore1p?: boolean;
  color?: ColorVariant;
}

const Input = ({
  className,
  type,
  onValueChange: onValueChangeProp,
  onChange,
  ignore1p = true,
  color: colorProp = "default",
  ...props
}: InputProps) => {
  const colorContext = React.use(ColorContext);
  const color = colorProp || colorContext;
  const onValueChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e);
      onValueChangeProp?.(e.target.value);
    },
    [onValueChangeProp, onChange],
  );
  return (
    <input
      type={type}
      data-1p-ignore={ignore1p}
      className={cn(
        "text-slate-normal relative",
        "h-10!",
        "flex h-10 w-full rounded-md border px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-slate-normal placeholder:text-slate-dim focus-visible:outline-hidden focus-visible:ring-2  focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        theme.input[color === "slate" ? "default" : color],
        className,
      )}
      onChange={onValueChange}
      {...props}
    />
  );
};
Input.displayName = "Input";

export type NumberInputProps = Omit<NumberFieldProps, "onChange" | "value"> & {
  onValueChange?: (v: number) => void;
  value?: number;
  placeholder?: string;
  min?: number;
  max?: number;
  step?: number;
  ref?: React.Ref<HTMLInputElement>;
  onPointerDown?: (e: React.PointerEvent) => void;
  onTouchStart?: (e: React.TouchEvent) => void;
  size?: "small" | "regular";
};

export function NumberInput({
  value,
  onValueChange,
  className,
  placeholder,
  min,
  max,
  step,
  ref,
  onPointerDown,
  onTouchStart,
  size = "regular",
  ...props
}: NumberInputProps) {
  const color = React.use(ColorContext) || "default";
  const buttonClass = cn(
    "flex-1 flex items-center justify-center",
    theme.numberInputButton[color],
  );

  return (
    <NumberField
      value={value}
      onChange={onValueChange}
      {...props}
      minValue={min}
      maxValue={max}
      step={step}
      className={cn(className, "min-w-0")}
    >
      <Group
        className={({ isFocusWithin }) => {
          return cn(
            "text-slate-normal overflow-hidden relative",
            "flex w-full rounded-md border pl-3 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-slate-normal placeholder:text-slate-dim disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
            size === "small" && "h-8 text-sm",
            size === "regular" && "h-10",
            theme.input[color],
            isFocusWithin && "outline-hidden ring-2 ring-offset-2",
          );
        }}
        onPointerDown={onPointerDown}
        onTouchStart={onTouchStart}
      >
        <ReactAriaInput
          ref={ref}
          data-1p-ignore
          className={cn(
            "w-auto",
            "py-2",
            "text-slate-normal bg-transparent outline-hidden flex-1 min-w-0",
            size === "regular" && "h-10",
          )}
          placeholder={placeholder}
        />
        <div
          className={cn(
            "flex flex-col shrink-0 border-l w-6",
            theme.numberInputBorder[color],
          )}
        >
          <Button slot="increment" className={buttonClass}>
            <ChevronUpIcon className="h-3 w-3" />
          </Button>
          <div
            className={cn("w-full border-b", theme.numberInputBorder[color])}
          />
          <Button slot="decrement" className={buttonClass}>
            <ChevronDownIcon className="h-3 w-3" />
          </Button>
        </div>
      </Group>
    </NumberField>
  );
}

export { Input };
